import { createTheme } from '@mui/material/styles'
import colors from './Colors'

const modeColor = (mode, light, dark) => (mode === 'light' ? light : dark)
const getPalette = (mode) => ({
  mode,
  content: {
    main: modeColor(mode, '#fff', '#303030'),
    contrastText: modeColor(mode, '#000', '#fff'),
  },
  primary: {
    main: modeColor(mode, colors.primary, colors.primaryLight3),
  },
  primaryLight: {
    main: modeColor(mode, colors.primaryLight, colors.primaryLight),
  },
  highlight: {
    main: modeColor(mode, colors.highlight, colors.highlightDark),
  },
  secondary: {
    main: colors.secondary,
    contrastText: '#fff',
  },
  accent: {
    main: colors.accent,
    contrastText: '#fff',
  },
  info: {
    main: colors.info,
    dark: colors.infoDark,
    contrastText: '#fff',
  },
  successLight: {
    main: colors.successLight,
    dark: colors.successDark,
    contrastText: '#fff',
    type: 'rgb',
  },
  errorLight: {
    main: colors.errorLight,
    contrastText: '#fff',
    type: 'rgb',
  },
  warningLight: {
    main: colors.warningLight,
    contrastText: '#fff',
    type: 'rgb',
  },
  grayLight: {
    main: colors.grayLight,
    contrastText: '#000',
    type: 'rgb',
  },
  grayMedium: {
    main: colors.grayMedium,
    contrastText: '#000',
    type: 'rgb',
  },
  grayDark: {
    main: colors.grayDark,
    contrastText: '#000',
    type: 'rgb',
  },
  contrastSuperLight: {
    main: modeColor(mode, '#f7f7f7', '#202020'),
    contrastText: modeColor(mode, '#000', '#fff'),
    type: 'rgb',
  },
  contrastLight: {
    main: modeColor(mode, '#f3f3f3', '#404040'),
    contrastText: modeColor(mode, '#000', '#fff'),
    type: 'rgb',
  },
  contrastMedium: {
    main: modeColor(mode, '#e0e0e0', '#525252'),
    contrastText: modeColor(mode, '#000', '#fff'),
    type: 'rgb',
  },
  contrastHigh: {
    main: modeColor(mode, '#aaa', '#bbb'),
    contrastText: modeColor(mode, '#000', '#fff'),
    type: 'rgb',
  },
  white: {
    main: '#fff',
    contrastText: '#000',
  },
  label: {
    main: modeColor(mode, 'rgba(0, 0, 0, 0.6)', 'rgba(255, 255, 255, 0.7)'),
  },
  menu: {
    main: modeColor(mode, colors.primary, '#221212'),
    accent: modeColor(mode, colors.primaryLight3, colors.primaryLight3),
    secondary: modeColor(mode, colors.grayMedium, '#bbb'),
    hasActive: modeColor(mode, colors.primaryLight2, '#332222'),
    active: modeColor(mode, colors.primaryLight3, '#443333'),
    selected: modeColor(mode, '#fff', '#aaa'),
    contrastText: modeColor(mode, '#fff', '#fff'),
  },
  navbar: {
    main: modeColor(mode, '#f3f3f3', '#202020'),
    contrastText: modeColor(mode, '#000', '#fff'),
    shadow: modeColor(mode, '#666', '#000'),
  },
  changelistToolbar: {
    main: modeColor(mode, '#f7f7f7', '#202020'),
    contrastText: modeColor(mode, '#000', '#fff'),
  },
  sidebar: {
    main: modeColor(mode, '#f3f3f3', '#202020'),
    contrastText: modeColor(mode, '#000', '#fff'),
    shadow: modeColor(mode, '#666', '#000'),
  },
  aside: {
    main: modeColor(mode, '#464646', '#353535'),
    accent: modeColor(mode, colors.primaryLight3, colors.primaryLight3),
    secondary: modeColor(mode, colors.grayMedium, '#ccc'),
    hasActive: modeColor(mode, '#575757', '#464646'),
    active: modeColor(mode, '#5d5d5d', '#565656'),
    selected: modeColor(mode, '#fff', '#aaa'),
    contrastText: modeColor(mode, '#fff', '#fff'),
  },
  trHover: {
    main: modeColor(mode, '#ebebeb', '#202020'),
    contrastText: modeColor(mode, '#000', '#fff'),
  },
  dashbaordStatAll: {
    main: modeColor(mode, colors.dashboardStatAll, colors.dashboardStatAll),
    dark: modeColor(mode, colors.dashboardStatAllDark, colors.dashboardStatAllDark),
    contrastText: modeColor(mode, '#fff', '#fff'),
  },
  dashbaordStatWorking: {
    main: modeColor(mode, colors.dashboardStatWorking, colors.dashboardStatWorking),
    dark: modeColor(mode, colors.dashboardStatWorkingDark, colors.dashboardStatWorkingDark),
    contrastText: modeColor(mode, '#fff', '#fff'),
  },
  dashbaordStatNotWorking: {
    main: modeColor(mode, colors.dashboardStatNotWorking, colors.dashboardStatNotWorking),
    dark: modeColor(mode, colors.dashboardStatNotWorkingDark, colors.dashboardStatNotWorkingDark),
    contrastText: modeColor(mode, '#fff', '#fff'),
  },
  dashbaordStatNotActive: {
    main: modeColor(mode, colors.dashboardStatNotActive, colors.dashboardStatNotActive),
    dark: modeColor(mode, colors.dashboardStatNotActiveDark, colors.dashboardStatNotActiveDark),
    contrastText: modeColor(mode, '#fff', '#fff'),
  },
  chart: {
    main: modeColor(mode, '#fff', '#202020'),
    contrastText: modeColor(mode, '#000', '#fff'),
  },
  resizableBorder: {
    main: modeColor(mode, 'rgba(0, 0, 0, .1)', 'rgba(0, 0, 0, .4)'),
  },
  gradientScaleBorder: {
    main: modeColor(mode, '#000', '#303030'),
  },
})

function container() {
  // Use the fullscreen element if in fullscreen mode, otherwise just the document's body
  return document.fullscreenElement ?? document.body
}

const getTheme = (mode) => {
  const theme = createTheme({
    palette: getPalette(mode),
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
    },
    overrides: {
      MuiTableCell: {
        sizeSmall: {
          padding: '4px 8px',
        },
      },
    },
    components: {
      MuiMenu: {
        defaultProps: {
          container,
        },
      },
      MuiTooltip: {
        defaultProps: {
          PopperProps: {
            container,
          },
        },
      },
    },
  })
  return theme
}

export default getTheme
